import { Input, Kbd } from '@wtfalch/design';
import { forwardRef, useState } from 'react';

import { suggestDue, todayIso } from './dates.ts';
import { parseTitle } from './source.ts';

/**
 * The add-a-task line. The words are read for a day ("Call the bank Friday"):
 * the day becomes the due date and, when it ends the sentence, leaves the title.
 */
export const Quick = forwardRef<
  HTMLInputElement,
  {
    label: string;
    placeholder: string;
    onAdd: (task: { title: string; dueOn: string | null }) => Promise<void>;
    today?: string;
  }
>(function Quick({ label, placeholder, onAdd, today = todayIso() }, ref) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    const when = suggestDue(text, today);
    const ends = when && text.slice(when.start + when.phrase.length).trim() === '';
    const rest = when && ends ? text.slice(0, when.start) : text;
    const parsed = parseTitle(rest.trim() === '' ? text : rest);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onAdd({ title: parsed.title, dueOn: when?.dueOn ?? null });
      setText('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The task was not added.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tasks-quick">
      <Input
        ref={ref}
        block
        icon="plus"
        aria-label={label}
        placeholder={placeholder}
        value={text}
        disabled={busy}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? 'tasks-quick-error' : undefined}
        trailing={<Kbd>Enter</Kbd>}
        onChange={(e) => {
          setText(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void submit();
          }
        }}
      />
      {/* The space is always there: an error must not move the list below it. */}
      <p id="tasks-quick-error" className="tasks-quick-error" role="alert">
        {error ?? ''}
      </p>
    </div>
  );
});
